import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Plus, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import ActivityImagePreview from '@/components/ActivityImagePreview';
import CustomImageUpload from '@/components/CustomImageUpload';
import PredefinedImageSelector from '@/components/PredefinedImageSelector';
import ActivityCategorySelector from '@/components/ActivityCategorySelector';
import ImageUploadErrorBoundary from '@/components/ImageUploadErrorBoundary';
import { SortableStepItem } from '@/components/SortableStepItem';
import { SortableTipItem } from '@/components/SortableTipItem';
import { useActivity } from '@/hooks/useActivity';
import { useUpdateActivity } from '@/hooks/useUpdateActivity';
import { useCategories } from '@/hooks/useActivities';
import { activityApi } from '@/services/adminApi';
import {
  type Tag,
  type Transition,
  type CreateActivityFormData,
  PLAY_TYPE_OPTIONS,
  PREP_NEEDED_OPTIONS,
  AGE_GROUP_OPTIONS,
} from '@/types/activity';
import type { ActivityImageValue } from '@/types/upload';

// ============================================
// Form Validation (reused from NewActivityPage)
// ============================================

interface FormErrors {
  title?: string;
  description?: string;
  duration?: string;
  playType?: string;
  prepNeeded?: string;
  steps?: string;
  tips?: string;
  whyItWorks?: string;
  tags?: string;
}

function validateForm(data: CreateActivityFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.title.trim()) {
    errors.title = 'Title is required';
  } else if (data.title.length > 255) {
    errors.title = 'Title must be 255 characters or less';
  }

  if (!data.description.trim()) {
    errors.description = 'Description is required';
  } else if (data.description.length > 1000) {
    errors.description = 'Description must be 1000 characters or less';
  }

  if (data.duration === '' || data.duration < 1) {
    errors.duration = 'Duration must be at least 1 minute';
  } else if (data.duration > 600) {
    errors.duration = 'Duration must be 600 minutes or less';
  }

  if (!data.playType) {
    errors.playType = 'Play type is required';
  }

  if (!data.prepNeeded) {
    errors.prepNeeded = 'Prep needed is required';
  }

  const validSteps = data.steps.filter((s) => s.text.trim() !== '');
  if (validSteps.length === 0) {
    errors.steps = 'At least 1 step is required';
  }

  const validTips = data.tips.filter((t) => t.text.trim() !== '');
  if (validTips.length === 0) {
    errors.tips = 'At least 1 tip is required';
  }

  if (!data.whyItWorks.text.trim()) {
    errors.whyItWorks = 'Why it works explanation is required';
  }

  if (data.tagIds.length === 0) {
    errors.tags = 'At least 1 tag is required';
  }

  return errors;
}

  // Form element styling constants (matching NewActivityPage)
  const inputClassName = "w-full px-4 py-3 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 placeholder-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors";
  const textareaClassName = "w-full px-3 py-2 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 placeholder-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors resize-none";
  const selectClassName = "w-full px-3 py-2 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors";


// ============================================
// Main Component
// ============================================

export default function EditActivityPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Fetch existing activity data
  const { data: activity, isLoading, error } = useActivity(id!);
  const updateMutation = useUpdateActivity(id!);

  // Form state
  const [formData, setFormData] = useState<CreateActivityFormData | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [availableTransitions, setAvailableTransitions] = useState<Transition[]>([]);

  // Categories
  const { data: categoriesData } = useCategories();
  const availableCategories = categoriesData?.data || [];

  // Image type switching state
  const [imageWarning, setImageWarning] = useState<string | null>(null);
  const [lastCustom, setLastCustom] = useState<ActivityImageValue | null>(null);
  const [lastPredefined, setLastPredefined] = useState<ActivityImageValue | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load tags on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await activityApi.getTags();
        setAvailableTags(response.data);
      } catch (err) {
        toast.error('Failed to load tags');
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    const loadTransitions = async () => {
      try {
        const response = await activityApi.getTransitions();
        setAvailableTransitions(response.data);
      } catch (err) {
        toast.error('Failed to load time periods');
      }
    };
    loadTransitions();
  }, []);

  // Populate form when activity loads
  useEffect(() => {
    if (activity) {
      const activityData = activity.data;
      // Build ActivityImageValue from activity data using imageType enum first, then legacy fallback
      const imageValue =
        activityData.imageType === 'custom' && activityData.documentId
          ? { type: 'custom' as const, documentId: activityData.documentId, viewUrl: activityData.imageUrl || '' }
          : activityData.imageType === 'category'
            ? { type: 'category' as const, categoryId: activityData.category?.id ? parseInt(activityData.category.id, 10) : undefined, viewUrl: activityData.imageUrl || '' }
            : activityData.imageType === 'predefined' && activityData.predefinedActivityImageId
              ? { type: 'predefined' as const, predefinedActivityImageId: activityData.predefinedActivityImageId, viewUrl: activityData.imageUrl || '' }
              : activityData.predefinedActivityImageId
                ? { type: 'predefined' as const, predefinedActivityImageId: activityData.predefinedActivityImageId, viewUrl: activityData.imageUrl || '' }
                : activityData.documentId && activityData.imageUrl
                  ? { type: 'custom' as const, documentId: activityData.documentId, viewUrl: activityData.imageUrl }
                  : null;

      if (imageValue?.type === 'custom') setLastCustom(imageValue);
      if (imageValue?.type === 'predefined') setLastPredefined(imageValue);

      setFormData({
        title: activityData.title,
        description: activityData.description,
        image: imageValue,
        documentId: activityData.documentId || null,
        predefinedActivityImageId: activityData.predefinedActivityImageId || null,
        imageType: activityData.imageType ?? null,
        duration: activityData.duration,
        playType: activityData.playType as 'solo_play' | 'play_together',
        prepNeeded: activityData.prepNeeded as 'no_prep_needed' | 'prep_needed',
        steps: activityData.steps.length > 0
          ? activityData.steps.map(step => ({ ...step, id: crypto.randomUUID() }))
          : [{ text: '', emphasis: '', id: crypto.randomUUID() }],
        tips: activityData.tips.length > 0
          ? activityData.tips.map(tip => ({ ...tip, id: crypto.randomUUID() }))
          : [{ text: '', boldText: '', id: crypto.randomUUID() }],
        whyItWorks: activityData.whyItWorks,
        tagIds: activityData.tags.map((t) => parseInt(t.id, 10)),
        transitionIds: activityData.transitions?.map((t) => parseInt(t.id, 10)) || [],
        ageGroupKeys: activityData.ageRanges?.map((ar) => `${ar.fromAge}-${ar.toAge}`) || [],
        categoryId: activityData.category?.id ? parseInt(activityData.category.id, 10) : null,
      });
    }
  }, [activity]);

  // Loading state
  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-admin-accent-blue" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-600">Failed to load activity</p>
        <Button onClick={() => navigate('/admin/activities')}>
          Back to Activities
        </Button>
      </div>
    );
  }

  // ============================================
  // Event Handlers
  // ============================================

  const handleFieldChange = (field: keyof CreateActivityFormData, value: CreateActivityFormData[keyof CreateActivityFormData]) => {
    setFormData((prev) => prev ? ({ ...prev, [field]: value }) : null);
    // Clear error for this field
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleTagToggle = (tagId: number) => {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return null;
      const newTagIds = prev.tagIds.includes(tagId)
        ? prev.tagIds.filter((id) => id !== tagId)
        : [...prev.tagIds, tagId];
      return { ...prev, tagIds: newTagIds };
    });
  };

  const handleTransitionToggle = (transitionId: number) => {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return null;
      const newTransitionIds = prev.transitionIds.includes(transitionId)
        ? prev.transitionIds.filter((id) => id !== transitionId)
        : [...prev.transitionIds, transitionId];
      return { ...prev, transitionIds: newTransitionIds };
    });
  };

  const handleAgeGroupToggle = (key: string) => {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return null;
      const newKeys = prev.ageGroupKeys.includes(key)
        ? prev.ageGroupKeys.filter((k) => k !== key)
        : [...prev.ageGroupKeys, key];
      return { ...prev, ageGroupKeys: newKeys };
    });
  };

  const handleStepChange = (
    index: number,
    field: 'text' | 'emphasis',
    value: string
  ) => {
    if (!formData) return;
    
    setFormData((prev) => {
      if (!prev) return null;
      const newSteps = [...prev.steps];
      newSteps[index] = { ...newSteps[index], [field]: value };
      return { ...prev, steps: newSteps };
    });
  };

  const addStep = () => {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        steps: [...prev.steps, { text: '', emphasis: '', id: crypto.randomUUID() }],
      };
    });
  };

  const removeStep = (index: number) => {
    if (!formData || formData.steps.length <= 1) return;
    
    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        steps: prev.steps.filter((_, i) => i !== index),
      };
    });
  };

  const handleStepDragEnd = (event: DragEndEvent) => {
    if (!formData) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFormData((prev) => {
        if (!prev) return null;

        const oldIndex = prev.steps.findIndex((step) => step.id === active.id);
        const newIndex = prev.steps.findIndex((step) => step.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return prev;

        return {
          ...prev,
          steps: arrayMove(prev.steps, oldIndex, newIndex),
        };
      });
    }
  };

  const handleTipChange = (
    index: number,
    field: 'boldText' | 'text',
    value: string
  ) => {
    if (!formData) return;
    
    setFormData((prev) => {
      if (!prev) return null;
      const newTips = [...prev.tips];
      newTips[index] = { ...newTips[index], [field]: value };
      return { ...prev, tips: newTips };
    });
  };

  const addTip = () => {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        tips: [...prev.tips, { text: '', boldText: '', id: crypto.randomUUID() }],
      };
    });
  };

  const removeTip = (index: number) => {
    if (!formData || formData.tips.length <= 1) return;
    
    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        tips: prev.tips.filter((_, i) => i !== index),
      };
    });
  };

  const handleTipDragEnd = (event: DragEndEvent) => {
    if (!formData) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFormData((prev) => {
        if (!prev) return null;

        const oldIndex = prev.tips.findIndex((tip) => tip.id === active.id);
        const newIndex = prev.tips.findIndex((tip) => tip.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return prev;

        return {
          ...prev,
          tips: arrayMove(prev.tips, oldIndex, newIndex),
        };
      });
    }
  };

  const handleWhyItWorksBoldKeywordsChange = (value: string) => {
    if (!formData) return;

    const keywords = value
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        whyItWorks: {
          ...prev.whyItWorks,
          boldKeywords: keywords,
        },
      };
    });
  };

  // Image type activation handlers
  const handleActivateCustom = () => {
    if (lastCustom) {
      setFormData((prev) => prev ? { ...prev, image: lastCustom, imageType: 'custom', documentId: lastCustom.documentId ?? null, predefinedActivityImageId: null } : null);
      setImageWarning(null);
    } else {
      setImageWarning('Upload a custom image first');
    }
  };

  const handleActivatePredefined = () => {
    if (lastPredefined) {
      setFormData((prev) => prev ? { ...prev, image: lastPredefined, imageType: 'predefined', documentId: null, predefinedActivityImageId: lastPredefined.predefinedActivityImageId ?? null } : null);
      setImageWarning(null);
    } else {
      setImageWarning('Select a predefined image first');
    }
  };

  const handleActivateCategory = () => {
    const cat = availableCategories.find((c) => parseInt(c.id, 10) === formData.categoryId);
    if (cat?.imageName) {
      const catValue: ActivityImageValue = { type: 'category', categoryId: formData.categoryId!, viewUrl: cat.imageName };
      setFormData((prev) => prev ? { ...prev, image: catValue, imageType: 'category', documentId: null, predefinedActivityImageId: null } : null);
      setImageWarning(null);
    } else if (!formData.categoryId) {
      setImageWarning('Select a category first');
    } else {
      setImageWarning('This category has no image');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData) return;

    // Validate
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await updateMutation.mutateAsync(formData);
      toast.success('Activity updated successfully!');
      navigate(`/admin/activities/${id}`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to update activity';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-admin-dark-950 pb-12">
      {/* Header */}
      <div className="bg-admin-dark-800 border-b border-admin-slate-300/10 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/activities')}
              className="text-admin-slate-500 hover:text-admin-slate-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-admin-slate-400">
              Edit Activity
            </h1>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-admin-accent-blue hover:bg-admin-accent-blue/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-6xl mx-auto px-6 py-8"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title">Activity Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="Enter activity title..."
                  maxLength={255}
                  className={inputClassName}
                  required
                />
                {errors.title && (
                  <p className="text-sm text-red-600 mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleFieldChange('description', e.target.value)
                  }
                  placeholder="Describe the activity..."
                  maxLength={1000}
                  rows={4}
                  className={textareaClassName}
                  required
                />
                <p className="text-sm text-admin-slate-500 mt-1">
                  {formData.description.length}/1000 characters
                </p>
                {errors.description && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Image Section */}
              <ImageUploadErrorBoundary>
                {formData.image && 'type' in formData.image && (
                  <ActivityImagePreview
                    value={formData.image}
                    onRemove={() => {
                      setFormData((prev) => prev ? { ...prev, image: null, imageType: null, documentId: null, predefinedActivityImageId: null } : null);
                      setImageWarning(null);
                    }}
                  />
                )}

                <div className="space-y-4 rounded-lg border border-admin-slate-300/10 p-4">
                  <CustomImageUpload
                    isActive={formData.image && 'type' in formData.image ? formData.image.type === 'custom' : false}
                    onActivate={handleActivateCustom}
                    onUpload={(val) => {
                      setLastCustom(val);
                      setFormData((prev) => prev ? { ...prev, image: val, imageType: 'custom', documentId: val.documentId ?? null, predefinedActivityImageId: null } : null);
                      setImageWarning(null);
                    }}
                    warning={!(formData.image && 'type' in formData.image && formData.image.type === 'custom') ? imageWarning : null}
                  />

                  <PredefinedImageSelector
                    isActive={formData.image && 'type' in formData.image ? formData.image.type === 'predefined' : false}
                    selectedImageId={lastPredefined?.predefinedActivityImageId ?? null}
                    onActivate={handleActivatePredefined}
                    onSelect={(val) => {
                      setLastPredefined(val);
                      setFormData((prev) => prev ? { ...prev, image: val, imageType: 'predefined', documentId: null, predefinedActivityImageId: val.predefinedActivityImageId ?? null } : null);
                      setImageWarning(null);
                    }}
                    warning={!(formData.image && 'type' in formData.image && formData.image.type === 'predefined') ? imageWarning : null}
                  />
                </div>

                <div className="rounded-lg border border-admin-slate-300/10 p-4">
                  <ActivityCategorySelector
                    isActive={formData.image && 'type' in formData.image ? formData.image.type === 'category' : false}
                    categories={availableCategories}
                    selectedCategoryId={formData.categoryId}
                    onCategoryChange={(categoryId) => {
                      setFormData((prev) => {
                        if (!prev) return null;
                        if (prev.imageType === 'category') {
                          const cat = availableCategories.find(c => parseInt(c.id, 10) === categoryId);
                          return {
                            ...prev,
                            categoryId,
                            image: cat?.imageName
                              ? { type: 'category', categoryId: categoryId!, viewUrl: cat.imageName }
                              : prev.image,
                          };
                        }
                        return { ...prev, categoryId };
                      });
                    }}
                    onActivate={handleActivateCategory}
                    warning={!(formData.image && 'type' in formData.image && formData.image.type === 'category') ? imageWarning : null}
                  />
                </div>
              </ImageUploadErrorBoundary>

              <Separator />

              {/* Duration and Play Type Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (minutes) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    max="600"
                    value={formData.duration}
                    onChange={(e) =>
                      handleFieldChange(
                        'duration',
                        e.target.value ? parseInt(e.target.value, 10) : ''
                      )
                    }
                    placeholder="30"
                    className={inputClassName}
                    required
                  />
                  {errors.duration && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.duration}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="playType">Play Type *</Label>
                  <select
                    id="playType"
                    value={formData.playType}
                    onChange={(e) => handleFieldChange('playType', e.target.value)}
                    className={selectClassName}
                    required
                  >
                    <option value="">Select play type...</option>
                    {PLAY_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.playType && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.playType}
                    </p>
                  )}
                </div>
              </div>

              {/* Prep Needed */}
              <div>
                <Label htmlFor="prepNeeded">Preparation Required *</Label>
                <select
                  id="prepNeeded"
                  value={formData.prepNeeded}
                  onChange={(e) => handleFieldChange('prepNeeded', e.target.value)}
                  className={selectClassName}
                  required
                >
                  <option value="">Select preparation level...</option>
                  {PREP_NEEDED_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.prepNeeded && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.prepNeeded}
                  </p>
                )}
              </div>

              <Separator />

              <Separator />

              {/* Tags */}
              <div>
                <Label>Tags * (select at least one)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleTagToggle(parseInt(tag.id, 10))}
                      className={`px-4 py-2 rounded-md border transition-colors ${
                        formData.tagIds.includes(parseInt(tag.id, 10))
                          ? 'bg-admin-accent-blue text-white border-admin-accent-blue'
                          : 'bg-admin-dark-700 text-admin-slate-400 border-admin-slate-300/10 hover:border-admin-accent-blue'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
                {errors.tags && (
                  <p className="text-sm text-red-400 mt-2">{errors.tags}</p>
                )}
              </div>

              <Separator />

              {/* Time Periods */}
              <div>
                <Label>Time Periods (optional)</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                  {availableTransitions.map((transition) => (
                    <button
                      key={transition.id}
                      type="button"
                      onClick={() => handleTransitionToggle(parseInt(transition.id, 10))}
                      className={`px-4 py-2 rounded-md border transition-colors ${
                        formData.transitionIds.includes(parseInt(transition.id, 10))
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-admin-dark-700 text-admin-slate-400 border-admin-slate-300/10 hover:border-amber-500'
                      }`}
                    >
                      {transition.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Groups */}
              <div>
                <Label>Age Groups (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {AGE_GROUP_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => handleAgeGroupToggle(option.key)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        formData.ageGroupKeys.includes(option.key)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-admin-dark-700 text-admin-slate-400 border-admin-slate-300/10 hover:border-blue-400'
                      }`}
                    >
                      {option.label} yrs
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Steps Card */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Steps</CardTitle>
              <p className="text-sm text-admin-slate-500 mt-2">
                Drag steps to reorder them. At least one step is required.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleStepDragEnd}
              >
                <SortableContext
                  items={formData.steps.map((step) => step.id!)}
                  strategy={verticalListSortingStrategy}
                >
                  {formData.steps.map((step, index) => (
                    <SortableStepItem
                      key={step.id}
                      id={step.id!}
                      step={step}
                      index={index}
                      onUpdate={handleStepChange}
                      onRemove={removeStep}
                      canRemove={formData.steps.length > 1}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <Button
                type="button"
                variant="outline"
                onClick={addStep}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Step
              </Button>

              {errors.steps && (
                <p className="text-sm text-red-600">{errors.steps}</p>
              )}
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Tips</CardTitle>
              <p className="text-sm text-admin-slate-500 mt-2">
                Drag tips to reorder them. At least one tip is required.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleTipDragEnd}
              >
                <SortableContext
                  items={formData.tips.map((tip) => tip.id!)}
                  strategy={verticalListSortingStrategy}
                >
                  {formData.tips.map((tip, index) => (
                    <SortableTipItem
                      key={tip.id}
                      id={tip.id!}
                      tip={tip}
                      index={index}
                      onUpdate={handleTipChange}
                      onRemove={removeTip}
                      canRemove={formData.tips.length > 1}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <Button
                type="button"
                variant="outline"
                onClick={addTip}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Tip
              </Button>

              {errors.tips && (
                <p className="text-sm text-red-600">{errors.tips}</p>
              )}
            </CardContent>
          </Card>

          {/* Why It Works Card */}
          <Card>
            <CardHeader>
              <CardTitle>Why It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="whyItWorksText">Explanation *</Label>
                <textarea
                  id="whyItWorksText"
                  value={formData.whyItWorks.text}
                  onChange={(e) =>
                    handleFieldChange('whyItWorks', {
                      ...formData.whyItWorks,
                      text: e.target.value,
                    })
                  }
                  placeholder="Explain why this activity is effective..."
                  maxLength={1000}
                  rows={4}
                  className={textareaClassName}
                  required
                />
                <p className="text-sm text-admin-slate-500 mt-1">
                  {formData.whyItWorks.text.length}/1000 characters
                </p>
                {errors.whyItWorks && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.whyItWorks}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="boldKeywords">Bold Keywords (optional)</Label>
                <Input
                  id="boldKeywords"
                  value={formData.whyItWorks.boldKeywords.join(', ')}
                  onChange={(e) =>
                    handleWhyItWorksBoldKeywordsChange(e.target.value)
                  }
                  placeholder="keyword1, keyword2, keyword3"
                  className={inputClassName}
                />
                <p className="text-sm text-admin-slate-500 mt-1">
                  Separate keywords with commas. These will appear in bold.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Save Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/activities')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-admin-accent-blue hover:bg-admin-accent-blue/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
